import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  type ReactNode,
} from "react";
import { useAppAuth } from "@/components/auth/AuthProvider";
import { usePostHog } from "posthog-js/react";
import { syncUser, type UserInfo } from "@/lib/userApi";
import { Baby, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import styles from "./UserSyncProvider.module.scss";

const USER_SYNCED_KEY = "goodbaby_user_synced";

interface UserSyncContextValue {
  isSynced: boolean;
  user: UserInfo | null;
}

const UserSyncContext = createContext<UserSyncContextValue>({
  isSynced: false,
  user: null,
});

export function useUserSync() {
  return useContext(UserSyncContext);
}

interface UserSyncProviderProps {
  children: ReactNode;
}

/**
 * UserSyncProvider
 *
 * Syncs user to database ONCE after login/signup.
 * Uses localStorage to remember if sync was already done for this user.
 * Only shows loading screen on first-time sync.
 */
export function UserSyncProvider({ children }: UserSyncProviderProps) {
  const {
    user: authUser,
    isLoaded: isAuthLoaded,
    getToken,
  } = useAppAuth();
  const posthog = usePostHog();
  const [isSynced, setIsSynced] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const syncAttempted = useRef(false);

  useEffect(() => {
    async function checkAndSyncUser() {
      if (!isAuthLoaded) return;

      // If no user logged in, nothing to sync
      if (!authUser) {
        posthog.reset();
        setIsSynced(false);
        return;
      }

      // Identify user in PostHog
      if (authUser.primaryEmailAddress?.emailAddress) {
        posthog.identify(authUser.primaryEmailAddress.emailAddress, {
          email: authUser.primaryEmailAddress.emailAddress,
          name: authUser.fullName || authUser.firstName,
          avatar: authUser.imageUrl,
        });
      }

      // Check if we already synced this user (cached in localStorage)
      const syncedUserId = localStorage.getItem(USER_SYNCED_KEY);
      if (syncedUserId === authUser.id) {
        console.log("[UserSync] User already synced (cached)");
        setIsSynced(true);
        return;
      }

      // Prevent duplicate sync attempts
      if (syncAttempted.current) return;
      syncAttempted.current = true;

      // First time - need to sync
      console.log("[UserSync] First time login - syncing user...");
      setIsSyncing(true);
      setError(null);

      try {
        const token = await getToken();
        if (!token) {
          throw new Error(
            "Your sign-in session is still loading. Please try again.",
          );
        }

        const syncedUser = await syncUser(token);

        console.log("[UserSync] User synced successfully");
        setUser(syncedUser);
        setIsSynced(true);
        // Cache the sync so we don't do it again
        localStorage.setItem(USER_SYNCED_KEY, authUser.id);
      } catch (err) {
        console.error("[UserSync] Error syncing user:", err);
        setError(err instanceof Error ? err.message : "Failed to sync user");
      } finally {
        setIsSyncing(false);
      }
    }

    checkAndSyncUser();
  }, [authUser, getToken, isAuthLoaded, posthog, retryCount]);

  // Clear sync cache on sign out
  useEffect(() => {
    if (isAuthLoaded && !authUser) {
      localStorage.removeItem(USER_SYNCED_KEY);
      syncAttempted.current = false;
    }
  }, [authUser, isAuthLoaded]);

  const handleRetry = () => {
    syncAttempted.current = false;
    setError(null);
    setRetryCount((count) => count + 1);
  };

  // Show loading screen ONLY during first-time sync
  if (isSyncing) {
    return (
      <div className={styles.page}>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <Baby size={32} strokeWidth={2} />
          </div>

          <div className={styles.spinnerContainer}>
            <Loader2 size={24} strokeWidth={2} className={styles.spinIcon} />
          </div>

          <h1 className={styles.title}>Setting up your account</h1>
          <p className={styles.subtitle}>This will only take a moment...</p>
        </div>
      </div>
    );
  }

  // Show error if sync failed
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.content}>
          <div className={styles.errorIconContainer}>
            <AlertCircle size={32} strokeWidth={2} />
          </div>

          <h1 className={styles.title}>Something went wrong</h1>
          <p className={styles.errorMessage}>{error}</p>

          <button
            type="button"
            className={styles.retryButton}
            onClick={handleRetry}
          >
            <RefreshCw size={20} strokeWidth={2} />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <UserSyncContext.Provider value={{ isSynced, user }}>
      {children}
    </UserSyncContext.Provider>
  );
}
